import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import EmployerSignupLayout, {
  NAVY, AMBER, WHITE, FONT_HEAD, FONT_BODY, StepPills,
} from './EmployerSignupLayout';

const inp = {
  width: '100%', padding: '11px 13px', fontSize: '0.95rem',
  border: '1.5px solid #E2E6EB', borderRadius: '8px',
  color: NAVY, outline: 'none', backgroundColor: WHITE,
};
const sel = { ...inp, cursor: 'pointer' };
const lbl = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: NAVY, marginBottom: '5px' };
const fld = { marginBottom: '1.1rem' };
const g2  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.1rem' };
const card = {
  backgroundColor: WHITE, borderRadius: '16px',
  boxShadow: '0 2px 24px rgba(0,0,0,0.07)',
  padding: '2rem', marginBottom: '1.25rem',
};

export default function EmployerStep2() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState({
    companyName: '', jobTitle: '', companySize: '', city: 'Chicago', state: 'IL',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleComplete = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) { navigate('/employer/signup'); return; }

    const fullName = session.user.user_metadata?.full_name || '';

    await supabase.from('employers').upsert({
      id: userId,
      full_name: fullName,
      email: session.user.email,
      company_name: form.companyName,
      job_title: form.jobTitle,
      company_size: form.companySize,
      city: form.city,
      state: form.state,
    }, { onConflict: 'id' });

    // Gives this employer an explicit employer_staff row (role: 'owner') so access checks
    // never have to special-case "is this uid literally the employers.id" -- every employer
    // login, owner or invited seat, is recognized the same uniform way from here on.
    const { error: staffError } = await supabase.rpc('bootstrap_employer_owner_staff');
    if (staffError) console.error('[EmployerStep2] Could not create owner staff row:', staffError.message);

    navigate('/employer/dashboard');
  };

  return (
    <EmployerSignupLayout
      step={2}
      tipText={t('employer.signup.step2.tip')}
      onBack={() => navigate('/employer/signup')}
      onContinue={handleComplete}
      continueLabel={t('employer.signup.step2.continueLabel')}
    >
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: AMBER, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '1rem' }}>
        {t('employer.signup.step2.eyebrow')}
      </div>

      <StepPills current={2} />

      <div style={card}>
        <h1 style={{ fontFamily: FONT_HEAD, fontSize: '2.1rem', color: NAVY, letterSpacing: '0.03em', marginBottom: '0.35rem' }}>
          {t('employer.signup.step2.title')}
        </h1>
        <p style={{ fontSize: '0.9rem', color: 'rgba(13,27,42,0.52)', marginBottom: '1.75rem', lineHeight: 1.5 }}>
          {t('employer.signup.step2.subtitle')}
        </p>

        {/* Company name */}
        <div style={fld}>
          <label style={lbl}>{t('employer.companyForm.companyNameLabel')}</label>
          <input style={inp} placeholder={t('employer.companyForm.companyNamePlaceholder')} value={form.companyName} onChange={e => set('companyName', e.target.value)} />
        </div>

        {/* Job title */}
        <div style={fld}>
          <label style={lbl}>{t('employer.companyForm.jobTitleLabel')}</label>
          <select style={sel} value={form.jobTitle} onChange={e => set('jobTitle', e.target.value)}>
            <option value="">{t('employer.companyForm.jobTitlePlaceholder')}</option>
            <option value="Owner">{t('employer.companyForm.jobTitleOptions.owner')}</option>
            <option value="HR Manager">{t('employer.companyForm.jobTitleOptions.hrManager')}</option>
            <option value="Hiring Manager">{t('employer.companyForm.jobTitleOptions.hiringManager')}</option>
            <option value="Recruiter">{t('employer.companyForm.jobTitleOptions.recruiter')}</option>
            <option value="Operations Manager">{t('employer.companyForm.jobTitleOptions.operationsManager')}</option>
            <option value="Other">{t('employer.companyForm.jobTitleOptions.other')}</option>
          </select>
        </div>

        {/* Company size */}
        <div style={fld}>
          <label style={lbl}>{t('employer.companyForm.companySizeLabel')}</label>
          <select style={sel} value={form.companySize} onChange={e => set('companySize', e.target.value)}>
            <option value="">{t('employer.companyForm.companySizePlaceholder')}</option>
            <option value="1–10 employees">{t('employer.companyForm.companySizeOptions.tier1')}</option>
            <option value="11–50 employees">{t('employer.companyForm.companySizeOptions.tier2')}</option>
            <option value="51–200 employees">{t('employer.companyForm.companySizeOptions.tier3')}</option>
            <option value="200+ employees">{t('employer.companyForm.companySizeOptions.tier4')}</option>
          </select>
        </div>

        {/* City + State */}
        <div style={g2}>
          <div>
            <label style={lbl}>{t('employer.companyForm.cityLabel')}</label>
            <input style={inp} placeholder="Chicago" value={form.city} onChange={e => set('city', e.target.value)} />
          </div>
          <div>
            <label style={lbl}>{t('employer.companyForm.stateLabel')}</label>
            <select style={sel} value={form.state} onChange={e => set('state', e.target.value)}>
              <option value="IL">Illinois</option>
              <option value="IN">Indiana</option>
              <option value="WI">Wisconsin</option>
              <option value="MI">Michigan</option>
              <option value="OH">Ohio</option>
              <option value="MO">Missouri</option>
              <option value="IA">Iowa</option>
              <option value="MN">Minnesota</option>
            </select>
          </div>
        </div>
      </div>
    </EmployerSignupLayout>
  );
}
